import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, RefreshCw, TrendingUp, AlertCircle, Sparkles, Wallet, KeyRound, Calendar, BedDouble,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Bar, Line,
} from "recharts";
import { toast } from "sonner";

// 12-month rolling forecast — confirmed bookings + active leases as the floor,
// historical occupancy applied to free nights as the expected line, plus a
// best-case ceiling. All values in KES-equivalent.

const fmtKES = (v) => `KES ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtKES_compact = (v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`;

export default function Forecast() {
  const navigate = useNavigate();
  const today = new Date();
  const defaultStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [start, setStart] = useState(defaultStart);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/housing/forecast?months=12&start=${start}`);
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load forecast");
    } finally { setLoading(false); }
  }, [start]);
  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.forecast.map((r) => ({
      label: r.month_label,
      Confirmed: r.confirmed_KES,
      Leases: r.leases_KES,
      Expected: r.expected_topup_KES,
      "Best case": r.total_best_case_KES,
      Expenses: -1 * (r.expenses_KES || 0),
      "Net expected": r.net_expected_KES,
      occupancy: r.occupancy_pct,
    }));
  }, [data]);

  if (loading || !data) return <div className="p-6 text-[#7A8A84]">Loading forecast…</div>;

  const t = data.totals;
  const baseline = data.baseline;
  const noHistory = baseline.months_of_data === 0;

  return (
    <div className="space-y-6 fade-in" data-testid="forecast-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#525C58]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope, sans-serif" }}>
            <TrendingUp className="h-8 w-8 text-[#40916C]" /> Annual forecast
          </h1>
          <p className="text-[#525C58] mt-1">Project the next 12 months of revenue using booked stays, lease commitments, and historical occupancy.</p>
          {data.exchange_rate?.usd_to_kes && (
            <p className="text-[11px] text-[#7A8A84] mt-1">
              All amounts in KES · USD → KES @ <span className="font-semibold text-[#1C1F1E]">{Number(data.exchange_rate.usd_to_kes).toFixed(2)}</span>
              {data.exchange_rate.is_default && <span className="ml-1 text-amber-700">(default rate)</span>}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" data-testid="start-input" />
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {noHistory && (
        <Card className="border-amber-300 bg-amber-50/50" data-testid="no-history-banner">
          <CardContent className="py-3 px-4 flex items-start gap-2 text-sm text-amber-900">
            <AlertCircle className="h-4 w-4 mt-0.5" />
            <p>
              <strong>Limited historical data.</strong> The forecast currently shows only your committed revenue (confirmed bookings + active leases).
              The <em>expected</em> and <em>best case</em> projections will populate once you have at least one full month of historical bookings. Keep coming back!
            </p>
          </CardContent>
        </Card>
      )}

      {/* Year totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <YearStat label="Floor (committed)" value={t.floor_KES} sub={`${fmtKES(t.confirmed_KES)} bookings · ${fmtKES(t.leases_KES)} leases`} color="#1B4332" testId="stat-floor" Icon={Wallet} />
        <YearStat label="Expected revenue" value={t.total_expected_KES} sub={`incl. ${fmtKES(t.expected_topup_KES)} estimated`} color="#2D6A4F" testId="stat-expected" Icon={Sparkles} />
        <YearStat label="Expenses (12mo)" value={t.expenses_KES || 0} sub={`templates + 6mo baseline`} color="#B5392F" testId="stat-expenses-fc" Icon={TrendingUp} />
        <YearStat label="Net expected" value={t.net_expected_KES ?? t.total_expected_KES} sub={`floor net: ${fmtKES(t.net_floor_KES ?? t.floor_KES)}`} color={(t.net_expected_KES || 0) >= 0 ? "#1B4332" : "#B5392F"} testId="stat-net" Icon={Calendar} />
      </div>

      {/* Chart */}
      <Card className="border-[#E8E8E6]" data-testid="forecast-chart-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-[#40916C]" />
            12-month projection
          </CardTitle>
          <CardDescription className="text-xs">
            Stacked bars = confirmed bookings + lease rent + historical-baseline top-up.
            Dashed line = best-case ceiling assuming 100% occupancy of free nights.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={chartData} margin={{ top: 12, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E6" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#7A8A84" }} />
              <YAxis tick={{ fontSize: 10, fill: "#7A8A84" }} tickFormatter={fmtKES_compact} />
              <Tooltip formatter={(v) => fmtKES(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Confirmed" stackId="rev" fill="#1B4332" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Leases" stackId="rev" fill="#7B61FF" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Expected" stackId="rev" fill="#40916C" radius={[3, 3, 0, 0]} />
              <Line dataKey="Best case" stroke="#B87D07" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3, fill: "#B87D07" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-[#E8E8E6]" data-testid="forecast-table-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BedDouble className="h-4 w-4 text-[#40916C]" /> Monthly breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table data-testid="forecast-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Confirmed</TableHead>
                  <TableHead className="text-right">Leases</TableHead>
                  <TableHead className="text-right">Expected top-up</TableHead>
                  <TableHead className="text-right text-[#1B4332]">Total expected</TableHead>
                  <TableHead className="text-right text-[#B5392F]">Expenses</TableHead>
                  <TableHead className="text-right text-[#1B4332]">Net expected</TableHead>
                  <TableHead className="text-right">Occupancy</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.forecast.map((r) => (
                  <TableRow key={r.period} data-testid={`forecast-row-${r.period}`}>
                    <TableCell>
                      <p className="font-medium">{r.month_label}</p>
                      <p className="text-[10px] text-[#7A8A84]">{r.committed_nights}/{r.capacity_nights} nights{r.leases_active_count > 0 ? ` · ${r.leases_active_count} lease${r.leases_active_count === 1 ? "" : "s"}` : ""}</p>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtKES(r.confirmed_KES)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.leases_KES > 0 ? <span className="text-purple-700">{fmtKES(r.leases_KES)}</span> : <span className="text-[#A3ACA8]">—</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-[#525C58]">
                      {r.expected_topup_KES > 0 ? fmtKES(r.expected_topup_KES) : <span className="text-[#A3ACA8]">—</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold text-[#1B4332]">{fmtKES(r.total_expected_KES)}</TableCell>
                    <TableCell className="text-right tabular-nums text-[#B5392F]">
                      {r.expenses_KES > 0 ? `−${fmtKES(r.expenses_KES)}` : <span className="text-[#A3ACA8]">—</span>}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${(r.net_expected_KES ?? 0) >= 0 ? "text-[#1B4332]" : "text-[#B5392F]"}`}>
                      {fmtKES(r.net_expected_KES ?? r.total_expected_KES)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        r.occupancy_pct >= 80 ? "bg-emerald-100 text-emerald-800"
                        : r.occupancy_pct >= 50 ? "bg-amber-100 text-amber-900"
                        : "bg-slate-100 text-slate-700"
                      }`}>
                        {r.occupancy_pct.toFixed(0)}%
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-[#F4F4F1] font-semibold border-t-2 border-[#1B4332]" data-testid="forecast-total-row">
                  <TableCell>12-month total</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtKES(t.confirmed_KES)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtKES(t.leases_KES)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtKES(t.expected_topup_KES)}</TableCell>
                  <TableCell className="text-right tabular-nums text-[#1B4332]">{fmtKES(t.total_expected_KES)}</TableCell>
                  <TableCell className="text-right tabular-nums text-[#B5392F]">−{fmtKES(t.expenses_KES || 0)}</TableCell>
                  <TableCell className={`text-right tabular-nums ${(t.net_expected_KES || 0) >= 0 ? "text-[#1B4332]" : "text-[#B5392F]"}`}>{fmtKES(t.net_expected_KES ?? t.total_expected_KES)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* How it's calculated */}
      <Card className="border-[#E8E8E6]" data-testid="methodology-card">
        <CardHeader>
          <CardTitle className="text-sm text-[#7A8A84] flex items-center gap-2 font-medium">
            <KeyRound className="h-3.5 w-3.5" /> How this forecast is built
          </CardTitle>
        </CardHeader>
        <CardContent className="text-[12px] text-[#525C58] space-y-2 leading-relaxed">
          <div><Badge className="bg-[#1B4332] text-white border-0 text-[10px] mr-1">Confirmed</Badge> Sum of (nights × nightly rate) for every confirmed or checked-in booking with stays touching that month.</div>
          <div><Badge className="bg-purple-100 text-purple-800 border border-purple-200 text-[10px] mr-1">Leases</Badge> Monthly rent + recurring add-ons for every active lease that covers any day of the month.</div>
          <div><Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] mr-1">Expected top-up</Badge> Free nights × historical occupancy ({baseline.avg_occupancy_pct.toFixed(0)}%) × historical avg nightly rate ({fmtKES(baseline.avg_nightly_rate_KES)}). Conservative.</div>
          <div><Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] mr-1">Best case</Badge> Free nights × historical avg nightly rate. Assumes every available night is filled — rarely happens, but useful as a ceiling.</div>
          <p className="text-[11px] text-[#7A8A84] pt-2 border-t border-[#E8E8E6]">USD-denominated bookings and leases are converted at the current org exchange rate. Historical baseline is computed from invoices and bookings issued before {data.from}.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function YearStat({ label, value, sub, color, testId, Icon }) {
  return (
    <Card className="border-[#E8E8E6]" data-testid={testId}>
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">{label}</p>
          {Icon && <Icon className="h-3.5 w-3.5" style={{ color }} />}
        </div>
        <p className="text-2xl font-bold mt-1 tabular-nums" style={{ color }}>{fmtKES(value)}</p>
        {sub && <p className="text-[10px] text-[#7A8A84] mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}
